/* ==========================================================================
   BDS history page — sections — scroll reveal, parallax, weld sparks, sand haze, lightbox
   No dependencies. Safe to load once per page (guards against double-init).
   ========================================================================== */
(function () {
  if (window.__bdsSections) return;
  window.__bdsSections = true;

  var motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  var reduced = function () { return motionQuery.matches; };

  /* ---------------------------------------------------------------- reveal */

  function initReveal(root) {
    var nodes = (root || document).querySelectorAll('[data-bds-reveal]:not([data-bds-reveal-ready])');
    if (!nodes.length) return;

    if (reduced() || !('IntersectionObserver' in window)) {
      nodes.forEach(function (el) {
        el.setAttribute('data-bds-reveal-ready', '');
        el.classList.add('bds-is-in');
      });
      return;
    }

    var io = new IntersectionObserver(function (entries, obs) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('bds-is-in');
        obs.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -12% 0px', threshold: 0.12 });

    nodes.forEach(function (el) {
      el.setAttribute('data-bds-reveal-ready', '');
      io.observe(el);
    });
  }

  /* -------------------------------------------------------------- parallax */

  var parallaxItems = [];
  var parallaxTicking = false;

  function initParallax(root) {
    var nodes = (root || document).querySelectorAll('[data-bds-parallax]:not([data-bds-parallax-ready])');
    nodes.forEach(function (el) {
      el.setAttribute('data-bds-parallax-ready', '');
      if (reduced()) return;
      parallaxItems.push({ el: el, amount: parseFloat(el.dataset.bdsParallax) || 0.12 });
    });
    if (parallaxItems.length) onScroll();
  }

  function onScroll() {
    if (parallaxTicking) return;
    parallaxTicking = true;
    requestAnimationFrame(function () {
      var vh = window.innerHeight;
      parallaxItems.forEach(function (item) {
        var rect = item.el.getBoundingClientRect();
        if (rect.bottom < -200 || rect.top > vh + 200) return;
        var progress = (rect.top + rect.height / 2 - vh / 2) / vh;
        var shift = progress * rect.height * item.amount * -1;
        item.el.style.transform = 'translate3d(0,' + shift.toFixed(2) + 'px,0)';
      });
      parallaxTicking = false;
    });
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });

  /* ------------------------------------------------------- canvas plumbing */

  function CanvasLayer(canvas) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d');
    this.running = false;
    this.last = 0;
  }

  /* The photo is object-fit cover/contain inside the section, so the part of
     it you can actually see moves and rescales with the viewport. Effects are
     placed against THAT rectangle, so an emitter parked on the welding torch
     stays on the torch at every width, and scales with the photo. */

  CanvasLayer.prototype.isNarrow = function () {
    return window.matchMedia('(max-width: 749px)').matches;
  };

  CanvasLayer.prototype.readAnchor = function () {
    var sel = this.canvas.dataset.anchor;
    this.anchorImg = null;
    if (!sel) return;
    var scope = this.canvas.closest('.bds-section') || document;
    this.anchorImg = scope.querySelector(sel);
    if (!this.anchorImg) return;

    var cs = getComputedStyle(this.anchorImg);
    this.fit = cs.objectFit || 'cover';
    var pos = (cs.objectPosition || '50% 50%').split(/\s+/);
    this.posX = parsePos(pos[0]);
    this.posY = parsePos(pos[1] === undefined ? pos[0] : pos[1]);

    var self = this;
    if (!this.anchorImg.complete) {
      this.anchorImg.addEventListener('load', function () { self.resize(); }, { once: true });
    }
  };

  CanvasLayer.prototype.box = function () {
    var img = this.anchorImg;
    if (!img || !img.naturalWidth || !img.naturalHeight) {
      return { x: 0, y: 0, w: this.w, h: this.h, unit: this.w ? clamp(this.w / 1400, 0.35, 2.5) : 1 };
    }
    var c = this.canvas.getBoundingClientRect();
    var r = img.getBoundingClientRect();
    var nw = img.naturalWidth, nh = img.naturalHeight;
    var scale = this.fit === 'contain'
      ? Math.min(r.width / nw, r.height / nh)
      : Math.max(r.width / nw, r.height / nh);
    var dw = nw * scale, dh = nh * scale;
    return {
      x: (r.left - c.left) + (r.width - dw) * this.posX,
      y: (r.top - c.top) + (r.height - dh) * this.posY,
      w: dw,
      h: dh,
      unit: clamp(dw / 1400, 0.35, 2.5)
    };
  };

  CanvasLayer.prototype.resize = function () {
    var rect = this.canvas.getBoundingClientRect();
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.w = rect.width;
    this.h = rect.height;
    this.canvas.width = Math.max(1, Math.floor(rect.width * dpr));
    this.canvas.height = Math.max(1, Math.floor(rect.height * dpr));
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.readAnchor();
  };

  CanvasLayer.prototype.bind = function () {
    var self = this;
    this._resize = function () { self.resize(); };
    this._mq = window.matchMedia('(max-width: 749px)');
    if (this._mq.addEventListener) {
      this._mqHandler = function () { self.resize(); };
      this._mq.addEventListener('change', this._mqHandler);
    }
    window.addEventListener('resize', this._resize, { passive: true });

    if ('IntersectionObserver' in window) {
      this.io = new IntersectionObserver(function (entries) {
        entries[0].isIntersecting ? self.start() : self.stop();
      }, { threshold: 0.01 });
      this.io.observe(this.canvas);
    } else {
      this.start();
    }

    this._vis = function () { document.hidden ? self.stop() : self.start(); };
    document.addEventListener('visibilitychange', this._vis);
  };

  CanvasLayer.prototype.start = function () {
    if (this.running || reduced()) return;
    this.running = true;
    this.last = performance.now();
    this.loop();
  };

  CanvasLayer.prototype.stop = function () {
    this.running = false;
    cancelAnimationFrame(this.frame);
  };

  CanvasLayer.prototype.loop = function () {
    var self = this;
    this.frame = requestAnimationFrame(function (t) { self.tick(t); });
  };

  CanvasLayer.prototype.destroy = function () {
    this.stop();
    window.removeEventListener('resize', this._resize);
    if (this._mq && this._mqHandler && this._mq.removeEventListener) {
      this._mq.removeEventListener('change', this._mqHandler);
    }
    document.removeEventListener('visibilitychange', this._vis);
    if (this.io) this.io.disconnect();
  };

  /* ----------------------------------------------------------- weld sparks */

  function Embers(canvas) {
    CanvasLayer.call(this, canvas);
    this.read();
    this.particles = [];
    this.glowPhase = 0;
    this.resize();
    this.bind();
  }
  Embers.prototype = Object.create(CanvasLayer.prototype);
  Embers.prototype.constructor = Embers;

  Embers.prototype.read = function () {
    var d = this.canvas.dataset;
    this.opts = {
      pattern: d.pattern || 'cone',
      originX: num(d.originX, 50) / 100,
      originY: num(d.originY, 50) / 100,
      count: num(d.count, 80),
      spread: num(d.spread, 60),
      angle: num(d.angle, -75),
      speed: num(d.speed, 1),
      size: num(d.size, 1.6),
      streak: num(d.streak, 1),
      life: num(d.life, 1.5),
      gravity: num(d.gravity, 0.16),
      drift: num(d.drift, 0.12),
      turbulence: num(d.turbulence, 0.3),
      burstRate: num(d.burstRate, 1.6),
      burstSize: num(d.burstSize, 12),
      glow: d.glow === 'true',
      glowSize: num(d.glowSize, 14) / 100,
      halo: d.halo !== 'false',
      bloom: num(d.bloom, 100) / 100,
      bloomAlpha: num(d.bloomAlpha, 50) / 100,
      color: hexToRgb(d.color || '#ffb547'),
      hot: hexToRgb(d.hot || '#fff5cf'),
      hotMix: num(d.hotMix, 35) / 100,
      scaleWithImage: d.scale !== 'false',
      // A separate mobile crop puts the arc somewhere else in the frame, so the
      // emitter can carry its own position and angle below 750px.
      mobile: d.mobile === 'true',
      originXm: num(d.originXm, num(d.originX, 50)) / 100,
      originYm: num(d.originYm, num(d.originY, 50)) / 100,
      angleM: num(d.angleM, num(d.angle, -75))
    };
  };

  Embers.prototype.origin = function () {
    var o = this.opts;
    var narrow = o.mobile && this.isNarrow();
    return {
      x: narrow ? o.originXm : o.originX,
      y: narrow ? o.originYm : o.originY,
      angle: narrow ? o.angleM : o.angle
    };
  };

  Embers.prototype.spawn = function (fast) {
    var o = this.opts;
    var box = this.frameBox || this.box();
    var unit = o.scaleWithImage ? box.unit : 1;
    var origin = this.origin();
    var angleDeg = origin.angle;
    var spread = o.spread;
    var speedMul = 1;
    var lifeMul = 1;

    switch (o.pattern) {
      case 'radial':                       // 360 degree shower
        angleDeg = Math.random() * 360;
        spread = 0;
        break;
      case 'fountain':                     // straight up, arcs back down
        angleDeg = -90;
        spread = o.spread * 0.6;
        lifeMul = 1.25;
        break;
      case 'fan':                          // wide flat sweep
        spread = Math.max(o.spread, 120);
        speedMul = 0.85;
        break;
      case 'jet':                          // tight, fast, long streaks
        spread = Math.min(o.spread, 16);
        speedMul = 1.9;
        lifeMul = 0.85;
        break;
    }

    var a = (angleDeg + (Math.random() - 0.5) * spread) * Math.PI / 180;
    var speed = (fast ? 210 : 80) * (0.45 + Math.random()) * o.speed * speedMul * unit;

    return {
      x: box.x + box.w * origin.x + (Math.random() - 0.5) * 12 * unit,
      y: box.y + box.h * origin.y + (Math.random() - 0.5) * 12 * unit,
      vx: Math.cos(a) * speed,
      vy: Math.sin(a) * speed,
      life: 0,
      max: o.life * (0.5 + Math.random() * 0.9) * lifeMul * (fast ? 0.75 : 1),
      size: o.size * (0.45 + Math.random() * 1.1) * unit,
      hot: Math.random() < o.hotMix
    };
  };

  Embers.prototype.tick = function (now) {
    if (!this.running) return;
    var dt = Math.min((now - this.last) / 1000, 0.05);
    this.last = now;

    var ctx = this.ctx;
    var o = this.opts;
    var box = this.box();
    this.frameBox = box;
    var unit = o.scaleWithImage ? box.unit : 1;

    ctx.clearRect(0, 0, this.w, this.h);
    ctx.globalCompositeOperation = 'lighter';

    if (o.glow) {
      this.glowPhase += dt * 5;
      var flicker = 0.55 + Math.sin(this.glowPhase) * 0.18 + Math.random() * 0.14;
      var glowAt = this.origin();
      var gx = box.x + box.w * glowAt.x;
      var gy = box.y + box.h * glowAt.y;
      var r = Math.max(box.w, box.h) * o.glowSize;
      var grad = ctx.createRadialGradient(gx, gy, 0, gx, gy, r);
      grad.addColorStop(0, rgba(o.hot, 0.5 * flicker));
      grad.addColorStop(0.35, rgba(o.color, 0.18 * flicker));
      grad.addColorStop(1, rgba(o.color, 0));
      ctx.fillStyle = grad;
      ctx.fillRect(gx - r, gy - r, r * 2, r * 2);
    }

    var deficit = o.count - this.particles.length;
    for (var i = 0; i < deficit; i++) this.particles.push(this.spawn(false));

    if (o.burstRate > 0 && Math.random() < dt * o.burstRate) {
      var n = Math.round(o.burstSize * (0.6 + Math.random() * 0.8));
      for (var b = 0; b < n; b++) this.particles.push(this.spawn(true));
    }

    for (var p = this.particles.length - 1; p >= 0; p--) {
      var s = this.particles[p];
      s.life += dt;
      if (s.life >= s.max || s.y > this.h + 60) {
        this.particles.splice(p, 1);
        continue;
      }
      s.vy += o.gravity * 60 * dt * unit;
      s.vx += (Math.random() - 0.5) * o.drift * 60 * dt * unit;
      s.vy += (Math.random() - 0.5) * o.turbulence * 30 * dt * unit;
      s.vx *= 0.995;
      s.x += s.vx * dt;
      s.y += s.vy * dt;

      var t = 1 - s.life / s.max;
      var alpha = Math.max(0, t) * (0.55 + Math.random() * 0.45);
      var col = s.hot ? o.hot : o.color;
      var w = Math.max(0.6, s.size * t);
      var trail = o.streak * (0.6 + t * 0.8) * unit;

      if (o.halo && o.bloom > 0 && o.bloomAlpha > 0) {
        var hr = Math.max(2, w * 3.4 * o.bloom);
        var hg = ctx.createRadialGradient(s.x, s.y, 0, s.x, s.y, hr);
        hg.addColorStop(0, rgba(col, alpha * o.bloomAlpha));
        hg.addColorStop(0.4, rgba(col, alpha * o.bloomAlpha * 0.35));
        hg.addColorStop(1, rgba(col, 0));
        ctx.fillStyle = hg;
        ctx.fillRect(s.x - hr, s.y - hr, hr * 2, hr * 2);
      }

      ctx.strokeStyle = rgba(col, alpha);
      ctx.lineWidth = w;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(s.x, s.y);
      ctx.lineTo(s.x - s.vx * 0.02 * trail, s.y - s.vy * 0.02 * trail);
      ctx.stroke();
    }

    ctx.globalCompositeOperation = 'source-over';
    this.loop();
  };

  /* ------------------------------------------------------------- sand haze */

  function Haze(canvas) {
    CanvasLayer.call(this, canvas);
    this.read();
    this.puffs = [];
    this.resize();
    this.bind();
  }
  Haze.prototype = Object.create(CanvasLayer.prototype);
  Haze.prototype.constructor = Haze;

  Haze.prototype.read = function () {
    var d = this.canvas.dataset;
    this.opts = {
      shape: d.shape || 'dune',
      color: hexToRgb(d.color || '#d9c39a'),
      color2: hexToRgb(d.color2 || '#f2e7d0'),
      opacity: num(d.opacity, 45) / 100,
      density: num(d.density, 26),
      size: num(d.size, 34) / 100,
      dispersion: num(d.dispersion, 40) / 100,
      lift: num(d.lift, 20) / 100,
      drift: num(d.drift, 22),
      direction: d.direction === 'left' ? -1 : 1,
      turbulence: num(d.turbulence, 30) / 100,
      coverage: num(d.coverage, 100) / 100,
      centerX: num(d.centerX, 50) / 100,
      band: num(d.height, 42) / 100,
      scaleWithImage: d.scale !== 'false',
      mobile: d.mobile === 'true',
      centerXm: num(d.centerXm, num(d.centerX, 50)) / 100,
      bandM: num(d.heightM, num(d.height, 42)) / 100
    };
  };

  /* The haze sits along the bottom of the PHOTO — if the image letterboxes or
     crops differently, the dust band moves and rescales with it. */
  Haze.prototype.region = function () {
    var box = this.box();
    var narrow = this.opts.mobile && this.isNarrow();
    var h = Math.max(8, box.h * (narrow ? this.opts.bandM : this.opts.band));
    return { x: box.x, y: box.y + box.h - h, w: box.w, h: h, unit: box.unit };
  };

  Haze.prototype.placeStatic = function () {
    var host = this.canvas.parentNode ? this.canvas.parentNode.querySelector('.bds-haze__static') : null;
    if (!host) return;
    var r = this.region();
    var c = this.canvas.getBoundingClientRect();
    if (!c.height) return;
    host.style.left = (r.x / c.width * 100) + '%';
    host.style.width = (r.w / c.width * 100) + '%';
    host.style.top = (r.y / c.height * 100) + '%';
    host.style.height = (r.h / c.height * 100) + '%';
    host.style.bottom = 'auto';
  };

  Haze.prototype.resize = function () {
    CanvasLayer.prototype.resize.call(this);
    this.placeStatic();
    this.seed();
  };

  Haze.prototype.seed = function () {
    this.puffs = [];
    for (var i = 0; i < this.opts.density; i++) this.puffs.push(this.makePuff(true));
  };

  Haze.prototype.baseline = function (x, r) {
    var o = this.opts;
    r = r || this.region();
    var bottom = r.y + r.h;
    var n = r.w ? (x - r.x) / r.w : 0.5;
    if (o.shape === 'flat') return bottom;
    if (o.shape === 'plume') {
      var center = (o.mobile && this.isNarrow()) ? o.centerXm : o.centerX;
      var dx = (n - center) / 0.32;
      return bottom - Math.exp(-dx * dx) * r.h * 0.42;
    }
    if (o.shape === 'wave') {
      return bottom - Math.sin(n * Math.PI * 3) * r.h * 0.16;
    }
    // dune — banked toward the middle, low at the edges
    return bottom - Math.sin(n * Math.PI) * r.h * 0.3
                  - Math.sin(n * Math.PI * 2.6) * r.h * 0.07;
  };

  Haze.prototype.makePuff = function (spawnAnywhere) {
    var o = this.opts;
    var r = this.region();
    var unit = o.scaleWithImage ? r.unit : 1;
    var center = (o.mobile && this.isNarrow()) ? o.centerXm : o.centerX;
    var half = o.coverage / 2;
    var lo = Math.max(0, center - half);
    var hi = Math.min(1, center + half);
    var nx = lo + Math.random() * (hi - lo);
    var x = r.x + nx * r.w;
    var edge = 60 * unit;
    return {
      x: spawnAnywhere ? x : (o.direction > 0 ? r.x + lo * r.w - edge : r.x + hi * r.w + edge),
      y: this.baseline(x, r) - Math.random() * r.h * o.dispersion,
      r: r.h * o.size * (0.45 + Math.random() * 1.1),
      a: 0.25 + Math.random() * 0.75,
      vx: o.drift * (0.4 + Math.random()) * o.direction * unit,
      vy: -r.h * o.lift * (0.05 + Math.random() * 0.2),
      phase: Math.random() * Math.PI * 2,
      hot: Math.random() < 0.35
    };
  };

  Haze.prototype.tick = function (now) {
    if (!this.running) return;
    var dt = Math.min((now - this.last) / 1000, 0.05);
    this.last = now;

    var ctx = this.ctx;
    var o = this.opts;
    var r = this.region();
    ctx.clearRect(0, 0, this.w, this.h);

    for (var i = 0; i < this.puffs.length; i++) {
      var p = this.puffs[i];
      p.phase += dt;
      p.x += p.vx * dt;
      p.y += p.vy * dt + Math.sin(p.phase * 0.8) * o.turbulence * 8 * dt;

      var margin = p.r + 40;
      if (p.x < r.x - margin || p.x > r.x + r.w + margin || p.y < r.y - margin) {
        this.puffs[i] = this.makePuff(false);
        continue;
      }

      var col = p.hot ? o.color2 : o.color;
      var alpha = o.opacity * p.a;
      var g = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.r);
      g.addColorStop(0, rgba(col, alpha));
      g.addColorStop(0.55, rgba(col, alpha * 0.42));
      g.addColorStop(1, rgba(col, 0));
      ctx.fillStyle = g;
      ctx.fillRect(p.x - p.r, p.y - p.r, p.r * 2, p.r * 2);
    }

    // dissolve the top edge into the photo, and keep the haze inside it
    ctx.globalCompositeOperation = 'destination-in';
    var fade = ctx.createLinearGradient(0, r.y, 0, r.y + r.h);
    fade.addColorStop(0, 'rgba(0,0,0,0)');
    fade.addColorStop(0.35, 'rgba(0,0,0,0.55)');
    fade.addColorStop(1, 'rgba(0,0,0,1)');
    ctx.fillStyle = fade;
    ctx.fillRect(r.x, r.y, r.w, r.h);
    ctx.globalCompositeOperation = 'source-over';

    this.loop();
  };


  /* ------------------------------------------------ ratio-mode overflow fit */
  /* In aspect-ratio mode the box height comes from the photo. If the overlay
     copy is taller than that box at a given viewport width, grow the section
     instead of clipping the text. */

  function initFit(root) {
    var nodes = (root || document).querySelectorAll('[data-bds-fit]:not([data-bds-fit-ready])');
    nodes.forEach(function (sec) {
      sec.setAttribute('data-bds-fit-ready', '');
      var inner = sec.querySelector('[data-bds-fit-content]');
      if (!inner) return;
      var applying = false;

      function check() {
        if (applying) return;
        applying = true;
        sec.style.minHeight = '';
        var need = Math.ceil(inner.getBoundingClientRect().height);
        var box = sec.getBoundingClientRect().height;
        if (need > box + 1) sec.style.minHeight = need + 'px';
        requestAnimationFrame(function () { applying = false; });
      }

      check();
      window.addEventListener('resize', check, { passive: true });
      window.addEventListener('load', check);
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(check);
      if ('ResizeObserver' in window) {
        var ro = new ResizeObserver(check);
        ro.observe(inner);
      }
    });
  }


  /* --------------------------------------------------------- magazine stack */

  function initMags(root) {
    var nodes = (root || document).querySelectorAll('[data-bds-mags]:not([data-bds-mags-ready])');
    nodes.forEach(function (stack) {
      stack.setAttribute('data-bds-mags-ready', '');
      var items = Array.prototype.slice.call(stack.querySelectorAll('.bds-mags__item'));

      items.forEach(function (item) {
        function front() {
          items.forEach(function (i) { i.classList.remove('is-front'); });
          item.classList.add('is-front');
        }
        item.addEventListener('mouseenter', front);
        item.addEventListener('focus', front);
        // On touch, the first tap brings the cover forward; the second follows the link.
        item.addEventListener('click', function (e) {
          if (!matchMedia('(hover: none)').matches) return;
          if (!item.classList.contains('is-front')) {
            // First tap surfaces the cover; the second opens it.
            e.preventDefault();
            e.stopImmediatePropagation();
            front();
          }
        });
      });

      stack.addEventListener('mouseleave', function () {
        items.forEach(function (i) { i.classList.remove('is-front'); });
      });
    });
  }

  /* -------------------------------------------------------------- timeline */

  function initTimeline(root) {
    var nodes = (root || document).querySelectorAll('[data-bds-timeline]:not([data-bds-timeline-ready])');
    nodes.forEach(function (wrap) {
      wrap.setAttribute('data-bds-timeline-ready', '');
      var rail = wrap.querySelector('[data-bds-timeline-rail]');
      if (!rail) return;

      var cards = Array.prototype.slice.call(rail.querySelectorAll('[data-bds-timeline-card]'));
      var tabs = Array.prototype.slice.call(wrap.querySelectorAll('[data-bds-timeline-year]'));
      var prev = wrap.querySelector('[data-bds-timeline-prev]');
      var next = wrap.querySelector('[data-bds-timeline-next]');
      var active = 0;
      var lockUntil = 0;

      function setActive(i) {
        active = Math.max(0, Math.min(cards.length - 1, i));
        tabs.forEach(function (tab, n) {
          var on = n === active;
          tab.classList.toggle('is-active', on);
          tab.setAttribute('aria-selected', on ? 'true' : 'false');
        });
        if (prev) prev.disabled = active === 0;
        if (next) next.disabled = active === cards.length - 1;
      }

      function goTo(i, smooth) {
        var card = cards[Math.max(0, Math.min(cards.length - 1, i))];
        if (!card) return;
        rail.scrollTo({
          left: card.offsetLeft - rail.offsetLeft,
          behavior: (smooth && !reduced()) ? 'smooth' : 'auto'
        });
        // The last cards can't scroll all the way left, so trust the click
        // over the scroll position for a moment.
        lockUntil = Date.now() + 900;
        setActive(i);
      }

      tabs.forEach(function (tab, i) {
        tab.addEventListener('click', function () { goTo(i, true); });
      });
      if (prev) prev.addEventListener('click', function () { goTo(active - 1, true); });
      if (next) next.addEventListener('click', function () { goTo(active + 1, true); });

      rail.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowRight') { e.preventDefault(); goTo(active + 1, true); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); goTo(active - 1, true); }
        if (e.key === 'Home') { e.preventDefault(); goTo(0, true); }
        if (e.key === 'End') { e.preventDefault(); goTo(cards.length - 1, true); }
      });

      // Keep the year tabs in sync while the user swipes or scrolls the rail:
      // the active milestone is whichever card sits closest to the rail's left edge.
      var syncing = false;
      function syncFromScroll() {
        if (syncing || Date.now() < lockUntil) return;
        syncing = true;
        requestAnimationFrame(function () {
          var maxScroll = rail.scrollWidth - rail.clientWidth;
          var nearest = 0;
          if (maxScroll > 0 && rail.scrollLeft >= maxScroll - 2) {
            nearest = cards.length - 1;                  // pinned at the end
          } else {
            var best = Infinity;
            for (var i = 0; i < cards.length; i++) {
              var d = Math.abs((cards[i].offsetLeft - rail.offsetLeft) - rail.scrollLeft);
              if (d < best) { best = d; nearest = i; }
            }
          }
          setActive(nearest);
          syncing = false;
        });
      }
      rail.addEventListener('scroll', syncFromScroll, { passive: true });

      setActive(0);
    });
  }


  /* -------------------------------------------------------------- lightbox */
  /* One overlay, reused. Items declare what they hold with data-lb-* attrs:
     a run of page images (flip with arrows, swipe or keyboard) or a PDF. */

  var LB = null;

  function buildLightbox() {
    if (LB) return LB;

    var el = document.createElement('div');
    el.className = 'bds-lb';
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-modal', 'true');
    el.setAttribute('aria-label', 'Full view');
    el.hidden = true;
    el.innerHTML =
      '<button class="bds-lb__close" type="button" aria-label="Close full view">&times;</button>' +
      '<button class="bds-lb__nav bds-lb__nav--prev" type="button" aria-label="Previous page">&#8592;</button>' +
      '<button class="bds-lb__nav bds-lb__nav--next" type="button" aria-label="Next page">&#8594;</button>' +
      '<div class="bds-lb__book">' +
        '<div class="bds-lb__edges bds-lb__edges--left" aria-hidden="true"></div>' +
        '<div class="bds-lb__edges bds-lb__edges--right" aria-hidden="true"></div>' +
        '<div class="bds-lb__stage"></div>' +
        '<div class="bds-lb__paper" aria-hidden="true"><span class="bds-lb__spine"></span><span class="bds-lb__grain"></span></div>' +
      '</div>' +
      '<div class="bds-lb__bar">' +
        '<div class="bds-lb__meta"><strong class="bds-lb__title"></strong><span class="bds-lb__caption"></span></div>' +
        '<div class="bds-lb__tools"><span class="bds-lb__count"></span><a class="bds-lb__link" href="#"></a></div>' +
      '</div>';
    document.body.appendChild(el);

    LB = {
      el: el,
      book: el.querySelector('.bds-lb__book'),
      stage: el.querySelector('.bds-lb__stage'),
      paper: el.querySelector('.bds-lb__paper'),
      edgesL: el.querySelector('.bds-lb__edges--left'),
      edgesR: el.querySelector('.bds-lb__edges--right'),
      title: el.querySelector('.bds-lb__title'),
      caption: el.querySelector('.bds-lb__caption'),
      count: el.querySelector('.bds-lb__count'),
      link: el.querySelector('.bds-lb__link'),
      prev: el.querySelector('.bds-lb__nav--prev'),
      next: el.querySelector('.bds-lb__nav--next'),
      pages: [],
      index: 0,
      opener: null
    };

    el.querySelector('.bds-lb__close').addEventListener('click', closeLightbox);
    el.addEventListener('click', function (e) { if (e.target === el) closeLightbox(); });
    LB.prev.addEventListener('click', function () { showPage(LB.index - 1, -1); });
    LB.next.addEventListener('click', function () { showPage(LB.index + 1, 1); });
    window.addEventListener('resize', function () { if (!LB.el.hidden) layoutPaper(); }, { passive: true });

    if ('ResizeObserver' in window) {
      LB.ro = new ResizeObserver(function () { layoutPaper(); });
    }

    document.addEventListener('keydown', function (e) {
      if (LB.el.hidden) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') showPage(LB.index + 1, 1);
      if (e.key === 'ArrowLeft') showPage(LB.index - 1, -1);
    });

    // Swipe between pages on touch.
    var startX = 0, startY = 0, tracking = false;
    LB.stage.addEventListener('touchstart', function (e) {
      if (!e.touches.length) return;
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      tracking = true;
    }, { passive: true });
    LB.stage.addEventListener('touchend', function (e) {
      if (!tracking || !e.changedTouches.length) return;
      tracking = false;
      var dx = e.changedTouches[0].clientX - startX;
      var dy = e.changedTouches[0].clientY - startY;
      if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy)) {
        var step = dx < 0 ? 1 : -1;
        showPage(LB.index + step, step);
      }
    }, { passive: true });

    return LB;
  }

  /* Sizes the paper furniture — spine shading, grain and the block of page
     edges — to whatever the current page image actually measures. */
  function layoutPaper() {
    if (!LB || LB.el.classList.contains('bds-lb--pdf')) return;
    var active = LB.stage.querySelector('.bds-lb__page.is-on');
    if (!active) return;
    var probe = active.getBoundingClientRect();
    if (probe.width < 8 || probe.height < 8) {
      LB.book.classList.remove('is-measured');   // stay hidden until the page has real size
      return;
    }
    LB.book.classList.add('is-measured');

    var b = LB.book.getBoundingClientRect();
    var r = active.getBoundingClientRect();
    var left = r.left - b.left, top = r.top - b.top;

    LB.paper.style.cssText = 'left:' + left + 'px;top:' + top + 'px;width:' + r.width + 'px;height:' + r.height + 'px;';

    var before = LB.index;
    var after = LB.pages.length - 1 - LB.index;
    edgeStack(LB.edgesL, Math.min(before, 6), -1, left, top, r.height);
    edgeStack(LB.edgesR, Math.min(after, 6), 1, left + r.width, top, r.height);
  }

  function edgeStack(host, count, dir, x, top, height) {
    host.innerHTML = '';
    host.style.cssText = 'left:' + x + 'px;top:' + top + 'px;height:' + height + 'px;';
    for (var i = 0; i < count; i++) {
      var sheet = document.createElement('span');
      var inset = (i + 1) * 1.6;
      sheet.className = 'bds-lb__sheet';
      sheet.style.cssText =
        'transform: translateX(' + (dir * (i + 1) * 2.2 - (dir < 0 ? 6 : 0)) + 'px);' +
        'top:' + inset + 'px; bottom:' + inset + 'px;' +
        'opacity:' + (0.92 - i * 0.09) + ';';
      host.appendChild(sheet);
    }
  }

  function showPage(i, dir) {
    if (!LB || !LB.pages.length) return;
    var from = LB.index;
    LB.index = Math.max(0, Math.min(LB.pages.length - 1, i));
    var imgs = LB.stage.querySelectorAll('.bds-lb__page');
    var turning = dir && from !== LB.index && !reduced() && LB.el.classList.contains('bds-lb--paper');

    imgs.forEach(function (img, n) { img.classList.toggle('is-on', n === LB.index); });

    if (turning) {
      // Forward: the page you were on peels away to the left.
      // Back: the page you're returning to swings in from the left.
      var leaf = dir > 0 ? imgs[from] : imgs[LB.index];
      var cls = dir > 0 ? 'is-turn-fwd' : 'is-turn-back';
      leaf.classList.remove('is-turn-fwd', 'is-turn-back');
      void leaf.offsetWidth;
      leaf.classList.add(cls);
      setTimeout(function () { leaf.classList.remove(cls); }, 760);
    }
    LB.count.textContent = LB.pages.length > 1 ? (LB.index + 1) + ' / ' + LB.pages.length : '';
    LB.prev.hidden = LB.pages.length < 2;
    LB.next.hidden = LB.pages.length < 2;
    LB.prev.disabled = LB.index === 0;
    LB.next.disabled = LB.index === LB.pages.length - 1;

    // Load the neighbouring pages ahead of the flip.
    [LB.index - 1, LB.index, LB.index + 1].forEach(function (n) {
      var img = imgs[n];
      if (img && !img.src && img.dataset.src) img.src = img.dataset.src;
    });

    requestAnimationFrame(layoutPaper);
  }

  function openLightbox(trigger) {
    var lb = buildLightbox();
    var d = trigger.dataset;

    lb.opener = trigger;
    lb.title.textContent = d.lbTitle || '';
    lb.caption.textContent = d.lbCaption || '';
    lb.stage.innerHTML = '';
    lb.pages = [];

    var paper = d.lbPaper !== 'false';
    lb.el.classList.toggle('bds-lb--paper', paper && !d.lbPdf);

    if (d.lbPdf) {
      lb.el.classList.add('bds-lb--pdf');
      var frame = document.createElement('iframe');
      frame.className = 'bds-lb__pdf';
      frame.src = d.lbPdf;
      frame.title = (d.lbTitle || 'Document') + ' (PDF)';
      lb.stage.appendChild(frame);
      lb.count.textContent = '';
      lb.prev.hidden = true;
      lb.next.hidden = true;
      lb.link.hidden = false;
      lb.link.href = d.lbPdf;
      lb.link.target = '_blank';
      lb.link.rel = 'noopener';
      lb.link.textContent = 'Open PDF in a new tab';
    } else {
      lb.el.classList.remove('bds-lb--pdf');
      lb.pages = (d.lbPages || '').split('|').filter(Boolean);
      lb.pages.forEach(function (src, n) {
        var img = document.createElement('img');
        img.className = 'bds-lb__page';
        img.alt = (d.lbTitle || '') + (lb.pages.length > 1 ? ' — page ' + (n + 1) : '');
        img.dataset.src = src;
        if (n < 2) img.src = src;
        img.addEventListener('load', function () { layoutPaper(); });
        lb.stage.appendChild(img);
        if (lb.ro) lb.ro.observe(img);
      });
      if (d.lbLink && d.lbLinkLabel) {
        lb.link.hidden = false;
        lb.link.href = d.lbLink;
        lb.link.target = '_blank';
        lb.link.rel = 'noopener';
        lb.link.textContent = d.lbLinkLabel;
      } else {
        lb.link.hidden = true;
      }
      showPage(0);
    }

    lb.el.hidden = false;
    document.documentElement.style.overflow = 'hidden';

    requestAnimationFrame(function () {
      lb.el.classList.add('is-open');
      lb.el.querySelector('.bds-lb__close').focus();
      layoutPaper();

      // Lift the cover off the stack: it flies from the thumbnail's position
      // and tips open like a magazine being picked up.
      if (paper && !d.lbPdf && !reduced() && trigger.getBoundingClientRect && lb.book.animate) {
        var t = trigger.getBoundingClientRect();
        var b = lb.book.getBoundingClientRect();
        if (t.width && b.width) {
          var sx = t.width / b.width, sy = t.height / b.height;
          var dx = (t.left + t.width / 2) - (b.left + b.width / 2);
          var dy = (t.top + t.height / 2) - (b.top + b.height / 2);
          lb.book.animate([
            { transform: 'translate(' + dx + 'px,' + dy + 'px) scale(' + sx + ',' + sy + ') rotateY(-18deg)', opacity: 0.4 },
            { transform: 'translate(0,0) scale(1) rotateY(0deg)', opacity: 1 }
          ], { duration: 620, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' });
        }
      }
    });

    if (!d.lbPdf) {
      [60, 260, 700].forEach(function (ms) { setTimeout(layoutPaper, ms); });
    }
  }

  function closeLightbox() {
    if (!LB || LB.el.hidden) return;
    LB.el.classList.remove('is-open');
    LB.book.classList.remove('is-measured');
    if (LB.ro) LB.ro.disconnect();
    document.documentElement.style.overflow = '';
    var opener = LB.opener;
    setTimeout(function () {
      LB.el.hidden = true;
      LB.stage.innerHTML = '';
      if (opener && opener.focus) opener.focus();
    }, 260);
  }

  function initLightboxTriggers(root) {
    var nodes = (root || document).querySelectorAll('[data-bds-lightbox]:not([data-bds-lb-ready])');
    nodes.forEach(function (el) {
      el.setAttribute('data-bds-lb-ready', '');
      el.addEventListener('click', function (e) {
        e.preventDefault();
        openLightbox(el);
      });
      el.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          openLightbox(el);
        }
      });
    });
  }

  /* ------------------------------------------------------- swipe indicators */

  function initSwipeDots(root) {
    var nodes = (root || document).querySelectorAll('[data-bds-swipe]:not([data-bds-swipe-ready])');
    nodes.forEach(function (track) {
      track.setAttribute('data-bds-swipe-ready', '');
      var dotsHost = document.querySelector('[data-bds-swipe-dots="' + track.dataset.bdsSwipe + '"]');
      if (!dotsHost) return;

      var items = Array.prototype.slice.call(track.children).filter(function (n) {
        return n.nodeType === 1 && !n.classList.contains('bds-tl__line');
      });
      if (items.length < 2) return;

      dotsHost.innerHTML = '';
      var dots = items.map(function (item, i) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'bds-dot' + (i === 0 ? ' is-on' : '');
        b.setAttribute('aria-label', 'Go to item ' + (i + 1));
        b.addEventListener('click', function () {
          track.scrollTo({ left: item.offsetLeft - track.offsetLeft, behavior: reduced() ? 'auto' : 'smooth' });
        });
        dotsHost.appendChild(b);
        return b;
      });

      var ticking = false;
      track.addEventListener('scroll', function () {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(function () {
          var best = Infinity, active = 0;
          items.forEach(function (item, i) {
            var d = Math.abs((item.offsetLeft - track.offsetLeft) - track.scrollLeft);
            if (d < best) { best = d; active = i; }
          });
          dots.forEach(function (dot, i) { dot.classList.toggle('is-on', i === active); });
          ticking = false;
        });
      }, { passive: true });
    });
  }

  /* ------------------------------------------------------------------ boot */

  function initCanvas(root, attr, Ctor) {
    var nodes = (root || document).querySelectorAll('[' + attr + ']:not([data-bds-canvas-ready])');
    nodes.forEach(function (canvas) {
      canvas.setAttribute('data-bds-canvas-ready', '');
      if (reduced()) { canvas.style.display = 'none'; return; }
      canvas.__layer = new Ctor(canvas);
    });
  }

  function clamp(v, lo, hi) { return Math.max(lo, Math.min(hi, v)); }

  function parsePos(token) {
    if (!token) return 0.5;
    if (token === 'left' || token === 'top') return 0;
    if (token === 'right' || token === 'bottom') return 1;
    if (token === 'center') return 0.5;
    if (token.indexOf('%') > -1) return parseFloat(token) / 100;
    return 0.5;
  }

  function num(v, fallback) {
    var n = parseFloat(v);
    return isNaN(n) ? fallback : n;
  }

  function hexToRgb(hex) {
    var h = String(hex).replace('#', '').trim();
    if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
    var int = parseInt(h, 16);
    if (isNaN(int)) return [255, 181, 71];
    return [(int >> 16) & 255, (int >> 8) & 255, int & 255];
  }

  function rgba(c, a) {
    return 'rgba(' + c[0] + ',' + c[1] + ',' + c[2] + ',' + a.toFixed(3) + ')';
  }

  function initAll(root) {
    initReveal(root);
    initParallax(root);
    initFit(root);
    initCanvas(root, 'data-bds-embers', Embers);
    initCanvas(root, 'data-bds-haze', Haze);
    initMags(root);
    initTimeline(root);
    initLightboxTriggers(root);
    initSwipeDots(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    e.target.querySelectorAll('[data-bds-embers],[data-bds-haze]').forEach(function (c) {
      if (c.__layer) c.__layer.destroy();
    });
  });

  if (motionQuery.addEventListener) {
    motionQuery.addEventListener('change', function () { initAll(document); });
  }

  window.BDSSections = { init: initAll, Embers: Embers, Haze: Haze };
})();
